import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import api from '@/lib/api';
import { countryCodes } from '@/lib/countries';
import { useLang } from '@/contexts/LangContext';
import { trackEvent } from '@/lib/tracking';

export default function ContactPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    country_code: '+971',
    subject: '',
    message: '',
    company_name: '',
    employees_range: '',
    work_email: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isCorporate = searchParams.get('type') === 'corporate';
  const presetSubject = searchParams.get('subject') || '';
  const presetMessage = searchParams.get('message') || '';

  useEffect(() => {
    setLoading(true);
    api.get('/content/contact-info').then(res => setContactInfo(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, [lang]);

  useEffect(() => {
    if (isCorporate) return;
    if (!presetSubject && !presetMessage) return;
    setForm((prev) => ({
      ...prev,
      subject: prev.subject || presetSubject,
      message: prev.message || presetMessage,
    }));
  }, [isCorporate, presetMessage, presetSubject]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isCorporate) {
      if (!form.company_name || !form.work_email || !form.phone || !form.employees_range) {
        toast.error(t('alerts.required_fields'));
        return;
      }
    } else {
      if (!form.name || !form.email || !form.subject || !form.message) {
        toast.error(t('alerts.required_fields'));
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = isCorporate ? {
        lead_type: 'corporate',
        company_name: form.company_name,
        employees_range: form.employees_range,
        work_email: form.work_email,
        phone: form.phone,
        country_code: form.country_code,
        notes: form.notes,
      } : form;
      await api.post('/contact', payload);
      const formType = isCorporate ? 'corporate_contact' : 'contact_message';
      const contactType = isCorporate ? 'corporate' : 'general';
      trackEvent(isCorporate ? 'corporate_contact_submit_success' : 'contact_form_submit_success', {
        form_id: 'contact_form',
        form_type: formType,
        source_page: isCorporate ? '/contact?type=corporate' : '/contact',
        source_component: 'ContactPage',
        route: '/contact',
        language: lang,
        contact_type: contactType,
        status: 'success',
      });
      setForm({
        name: '',
        email: '',
        phone: '',
        country_code: '+971',
        subject: '',
        message: '',
        company_name: '',
        employees_range: '',
        work_email: '',
        notes: '',
      });
      navigate('/thank-you/inquiry-received', {
        state: {
          form_type: formType,
          source_page: isCorporate ? '/contact?type=corporate' : '/contact',
          source_component: 'ContactPage',
          verified_submission: true,
        },
      });
    } catch {
      toast.error(t('contact.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const contactItems = contactInfo ? [
    { icon: MapPin, key: 'contact.visit', value: contactInfo.address || '' },
    { icon: Phone, key: 'contact.call', value: contactInfo.phone || '' },
    { icon: Mail, key: 'contact.email_us', value: contactInfo.email || '' },
    { icon: Clock, key: 'contact.hours', value: contactInfo.hours || '' },
  ] : [];

  return (
    <div data-testid="contact-page" className="bf-page-canvas bf-contact-page bg-[#f4f5f7] text-[#1c2333] dark:bg-[#1c2333] dark:text-[#f4f5f7]">
      <section className="bf-editorial-page-hero relative overflow-hidden py-10 sm:py-14" style={{
        backgroundImage: 'radial-gradient(1200px circle at 15% 10%, hsl(200 85% 92% / 0.45) 0%, transparent 55%)'
      }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Badge variant="secondary" className="bf-ui-pill mb-4">{t('contact.title')}</Badge>
            <h1 className="bf-page-display max-w-3xl">{t('contact.title')}</h1>
            <p className="bf-page-copy mt-4 max-w-xl text-[#1c2333]/72 dark:text-[#f4f5f7]/74">{t('contact.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2 space-y-6">
              {loading ? (
                [1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-2xl" />)
              ) : (
                contactItems.map(({ icon: Icon, key, value }) => (
                  <Card key={key} className="bf-contact-info-card rounded-2xl border-0 shadow-none">
                    <CardContent className="p-5 flex items-start gap-4">
                      <div className="bf-contact-info-icon h-11 w-11 rounded-xl flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></div>
                      <div><p className="bf-ui-label mb-1 text-[#6c8bc6]">{t(key)}</p><p className="text-sm leading-7 text-[#1c2333]/70 dark:text-[#f4f5f7]/72 whitespace-pre-line">{value}</p></div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            <Card className="bf-contact-form-card lg:col-span-3 rounded-2xl border-0 shadow-none">
              <CardContent className="p-6 sm:p-8">
                <h2 className="bf-subsection-display mb-6">{t('contact.send_message')}</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {isCorporate ? (
                    <>
                      <div><label className="text-sm font-medium mb-1.5 block">{t('contact.company_name')} *</label><Input className="rounded-lg" placeholder={t('contact.company_name')} value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} data-testid="contact-company-name-input" /></div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div><label className="text-sm font-medium mb-1.5 block">{t('contact.work_email')} *</label><Input type="email" className="rounded-lg" placeholder={t('contact.work_email')} value={form.work_email} onChange={e => setForm(f => ({ ...f, work_email: e.target.value }))} data-testid="contact-work-email-input" /></div>
                        <div><label className="text-sm font-medium mb-1.5 block">{t('contact.employees_range')} *</label>
                          <Select value={form.employees_range} onValueChange={v => setForm(f => ({ ...f, employees_range: v }))} data-testid="contact-employees-range-select">
                            <SelectTrigger className="rounded-lg"><SelectValue placeholder={t('contact.employees_range')} /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1_10">{t('contact.employees_1_10')}</SelectItem>
                              <SelectItem value="11_50">{t('contact.employees_11_50')}</SelectItem>
                              <SelectItem value="51_100">{t('contact.employees_51_100')}</SelectItem>
                              <SelectItem value="101_250">{t('contact.employees_101_250')}</SelectItem>
                              <SelectItem value="251_500">{t('contact.employees_251_500')}</SelectItem>
                              <SelectItem value="500_plus">{t('contact.employees_500_plus')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div><label className="text-sm font-medium mb-1.5 block">{t('auth.code')}</label>
                          <Select value={form.country_code} onValueChange={v => setForm(f => ({ ...f, country_code: v }))}><SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger><SelectContent>{countryCodes.map(c => <SelectItem key={c.code} value={c.code}>{c.code} {c.country}</SelectItem>)}</SelectContent></Select>
                        </div>
                        <div className="col-span-2"><label className="text-sm font-medium mb-1.5 block">{t('contact.phone')} *</label><Input className="rounded-lg" placeholder={t('contact.phone')} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="contact-phone-input" /></div>
                      </div>
                      <div><label className="text-sm font-medium mb-1.5 block">{t('contact.notes')}</label><Textarea className="rounded-lg min-h-[120px]" placeholder={t('contact.notes')} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="contact-notes-input" /></div>
                    </>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div><label className="text-sm font-medium mb-1.5 block">{t('contact.name')} *</label><Input className="rounded-lg" placeholder={t('contact.name')} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="contact-name-input" /></div>
                        <div><label className="text-sm font-medium mb-1.5 block">{t('contact.email')} *</label><Input type="email" className="rounded-lg" placeholder={t('contact.email')} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="contact-email-input" /></div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div><label className="text-sm font-medium mb-1.5 block">{t('auth.code')}</label>
                          <Select value={form.country_code} onValueChange={v => setForm(f => ({ ...f, country_code: v }))}><SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger><SelectContent>{countryCodes.map(c => <SelectItem key={c.code} value={c.code}>{c.code} {c.country}</SelectItem>)}</SelectContent></Select>
                        </div>
                        <div className="col-span-2"><label className="text-sm font-medium mb-1.5 block">{t('contact.phone')}</label><Input className="rounded-lg" placeholder={t('contact.phone')} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="contact-phone-input" /></div>
                      </div>
                      <div><label className="text-sm font-medium mb-1.5 block">{t('contact.subject')} *</label><Input className="rounded-lg" placeholder={t('contact.subject')} value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} data-testid="contact-subject-input" /></div>
                      <div><label className="text-sm font-medium mb-1.5 block">{t('contact.message')} *</label><Textarea className="rounded-lg min-h-[120px]" placeholder={t('contact.message')} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} data-testid="contact-message-input" /></div>
                    </>
                  )}
                  <Button type="submit" className="bf-premium-cta bf-ui-button w-full rounded-full h-12 bg-[#6c8bc6] text-white hover:bg-[#5876b0]" disabled={submitting} data-testid="contact-submit-button">{submitting ? t('contact.sending') : t('contact.send')}<Send className="ml-2 h-4 w-4" /></Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
